<template>
  <div class="calender-selector">
    <div class="header">
      <div class="timezone">
        <div class="input-wrap">
          <i class="isif isif-globe prefix" />
          <span class="input-inner">{{ timezone }}</span>
        </div>
      </div>
      <div class="month-info">
        {{ monthForDisplay }}
      </div>
      <div class="pager">
        <div class="input-wrap">
          <i class="isif isif-calendar prefix" />
          <div class="input-inner">
            <button
              :disabled="pager.page === 1"
              @click="switchPage(-1)"
            >
              <i class="isif isif-arrow-left" />
            </button>
            <button
              :disabled="pager.page === totalPage"
              @click="switchPage(1)"
            >
              <i class="isif isif-arrow-right" />
            </button>
          </div>
        </div>
      </div>
    </div>
    <div class="calendar-wrap">
      <div
        ref="calendar"
        class="calendar"
      >
        <div
          v-for="(calendar, i) in calendarForDisplay"
          :key="i"
          class="day-col"
          :class="{ 'is-today': calendar.dateString === today }"
        >
          <div class="date-info">
            <div class="week">
              {{ calendar.date | moment('ddd') }}
            </div>
            <div class="day">
              {{ calendar.date | moment('DD') }}
            </div>
          </div>
          <div
            v-for="time in activeRangeFormatted"
            :key="formatValue(calendar, time)"
            class="time-select"
            :class="{
              active: isActive(calendar, time),
              disabled: isDisabled(calendar, time)
            }"
            @click="select(calendar, time)"
          >
            {{ time }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { debounce } from 'lodash'

export default {
  name: 'ICalendar',
  props: {
    timezone: {
      type: String,
      default: ''
    },
    activeRange: {
      type: Array,
      default: () => []
    },
    activeTimes: {
      type: Array,
      default: () => []
    }
  },
  data: self => ({
    data: [],
    dayTotal: 91, // today + 90 days
    pager: {
      limit: 7,
      page: 1
    },
    selectedDate: '',
    // timezone: self.$moment.tz.guess(),
    today: self.$moment().format('YYYY-MM-DD'),
    resizeListener: null
  }),
  computed: {
    totalPage () {
      return Math.ceil(this.dayTotal / this.pager.limit)
    },
    monthForDisplay () {
      if (!this.calendarForDisplay.length) {
        return '--'
      }

      const remain = this.dayTotal % this.pager.limit
      const limit = this.pager.page === this.totalPage && remain !== 0 ? remain : this.pager.limit
      const dateFrom = this.calendarForDisplay[0]
      const dateTo = this.calendarForDisplay[limit - 1]

      const [year1, month1] = dateFrom.dateString.split('-')
      const [year2, month2] = dateTo.dateString.split('-')
      const monthFull1 = dateFrom.date.format('MMMM')
      const monthFull2 = dateTo.date.format('MMMM')

      if (month1 === month2) {
        return monthFull1 + ' ' + year1
      } else if (year1 === year2) {
        return monthFull1 + ' / ' + monthFull2 + ' ' + year1
      }
      return monthFull1 + ' ' + year1 + ' / ' + monthFull2 + ' ' + year2
    },
    calendarForDisplay () {
      const start = (this.pager.page - 1) * this.pager.limit
      const end = start + this.pager.limit
      return this.data.slice(start, end)
    },
    activeRangeFormatted () {
      return this.activeRange.map(time => this.$moment(time, 'kk:mm').format('HH:mm'))
    },
    activeTimesFormatted () {
      return this.activeTimes.map(datetime => this.$moment(datetime).format('YYYY-MM-DD HH:mm'))
    }
  },
  watch: {
    'pager.limit' () {
      this.pager.page = 1
    }
  },
  mounted () {
    this.initCalendar()
    this.resizeListener = debounce(this.onResize, 200)
    window.addEventListener('resize', this.resizeListener)
    // Force trigger at first
    this.onResize()
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.resizeListener)
  },
  methods: {
    initCalendar () {
      const calendar = []
      const startDay = this.$moment()
      const endDay = this.$moment().add(this.dayTotal - 1, 'days')

      const dateExplorer = startDay.clone().subtract(1, 'day')
      while (dateExplorer.isBefore(endDay, 'day')) {
        const date = dateExplorer.add(1, 'day').clone()
        calendar.push({
          date,
          dateString: date.format('YYYY-MM-DD')
        })
      }
      this.data = calendar
    },
    onResize () {
      const calendar = this.$refs.calendar
      if (!calendar) return

      // Wait children rendered
      this.$nextTick(() => {
        const calendarWidth = calendar.clientWidth
        const colWidth = calendar.children[0].clientWidth

        let limit = 7 // default = a week
        // At least show 2 cols
        while (colWidth * limit > calendarWidth && limit > 2) {
          limit--
        }
        this.pager.limit = limit
      })
    },
    switchPage (change) {
      const page = this.pager.page + change
      this.pager.page = Math.max(Math.min(page, this.totalPage), 1)
    },
    formatValue (date, time) {
      return date.dateString + ' ' + time
    },
    isActive (date, time) {
      return this.selectedDate === this.formatValue(date, time)
    },
    isDisabled (date, time) {
      const value = this.formatValue(date, time)
      if (!this.activeTimesFormatted.includes(value)) {
        return true
      }
      const target = this.$moment(value)
      // Convert to time in timezone, then output string in local date
      const nowInTz = this.$moment().tz(this.timezone).format('YYYY-MM-DD HH:mm:ss')
      // Then, init the moment instance again
      const nowInLocal = this.$moment(nowInTz)
      return target.isBefore(nowInLocal)
    },
    select (date, time) {
      if (this.isDisabled(date, time)) return
      const value = this.formatValue(date, time)
      this.selectedDate = value
      this.$emit('select', value)
    }
  }
}
</script>

<style lang="stylus" scoped>
@import '~css/addon/main'

.calender-selector {
  display: flex;
  flex-direction: column;
  height: 100%;
}

$input-height = 32px
.input-wrap {
  position: relative
  background: #FFF
  display: inline-block
  line-height: $input-height - 2px // border * 2
  border: 1px solid #C4C4C4
  border-radius: 5px
  overflow: hidden

  .prefix {
    position: absolute
    top: 7px // (30 - 6) /2
    left: 10px
  }
  .input-inner {
    position: relative
    color: #212121
    display: inline-block
    padding: 0 15px 0 36px
    text-shadow: none
  }
}

.header {
  flex: 0 0 66px
  height: 66px
  margin-bottom: 20px
  display: flex
  justify-content: center
  align-items: flex-end
  position: relative

  @media (min-width: $response) {
    flex: 0 0 36px
    height: 36px
    align-items: center
  }

  .month-info {
    font-size: 18px
    font-weight: bold
  }

  .timezone,
  .pager {
    height: $input-height
    position: absolute
    top: 0
    margin: auto

    @media (min-width: $response) {
      bottom: 0
    }
  }

  .timezone {
    left: 0
  }

  .pager {
    right: 0

    button {
      padding: 0
      width: 20px
      height: 20px
      color: #212121
      background: transparent
      border: none
      user-select: none

      &[disabled] {
        opacity: .25
        cursor: default
      }
    }
  }
}

.calendar-wrap {
  flex-grow: 1
  overflow: auto
}

.calendar {
  display: flex
  justify-content: center
}

.day-col {
  display: flex
  flex-direction: column
  flex: 0 0 100px
  height: 100%
  padding: 8px
  text-align: center

  &.is-today {
    background: rgba(#ACC5F3, .15)
  }
}

.date-info {
  margin-bottom: 10px

  .week {
    margin-bottom: 5px
  }
  .day {
    font-weight: bold
    font-size: 22px
  }
}

.time-select {
  line-height: 32px
  border: 1px solid #79BB74
  border-radius: 5px
  cursor: pointer
  box-shadow: 0 0 1px rgba(#79BB74, .75)
  user-select: none

  &:not(:last-child) {
    margin-bottom: 10px
  }
  &:hover {
    background: rgba(#79BB74, .1)
  }
  &.active {
    color: #40853B
    background: rgba(#79BB74, .4)
  }
  &.disabled {
    color: rgba(#212121, .2)
    border-color: #C4C4C4
    background: rgba(#C4C4C4, .15)
    box-shadow: none
    cursor: default
  }
}
</style>
